import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Switch } from "../ui/switch";
import { 
  Plus, Edit3, Trash2, Save, X, Search, Filter, 
  BookOpen, Users, Target, Award, Lightbulb, MessageSquare,
  Video, Mail, Copy, Star, Eye, Settings, CheckCircle,
  AlertCircle, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminMarketingManager = () => {
  // State management
  const [angles, setAngles] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAngle, setSelectedAngle] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [createMode, setCreateMode] = useState(false);
  const [currentTab, setCurrentTab] = useState('angles');
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSource, setSelectedSource] = useState('all');
  const [selectedAccessLevel, setSelectedAccessLevel] = useState('all');
  
  // Form state for creating/editing
  const [formData, setFormData] = useState({
    title: '',
    category: 'testimonial',
    source: 'custom',
    strategy_type: 'social_proof',
    dm_script: '',
    post_script: '',
    video_script: '',
    email_script: '',
    target_audience: [],
    industry_tags: [],
    effectiveness_score: 5.0,
    tone: 'professional',
    access_level: 'rookie',
    featured: false,
    active: true
  });

  useEffect(() => {
    fetchAngles();
    if (currentTab === 'templates') {
      fetchTemplates();
    }
  }, [currentTab, searchQuery, selectedCategory, selectedSource, selectedAccessLevel]);

  const fetchAngles = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: 1,
        page_size: 50,
        ...(searchQuery && { search: searchQuery }),
        ...(selectedCategory !== 'all' && { category: selectedCategory }),
        ...(selectedSource !== 'all' && { source: selectedSource })
      });

      const response = await fetch(`${BACKEND_URL}/api/marketing/angles?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (data.ok) {
        setAngles(data.angles);
      }
    } catch (error) {
      console.error('Error fetching marketing angles:', error);
      toast.error("Failed to load marketing angles");
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/marketing/templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (data.ok) {
        setTemplates(data.templates);
      }
    } catch (error) {
      console.error('Error fetching marketing templates:', error);
      toast.error("Failed to load marketing templates");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAngle = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/marketing/angles`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          target_audience: formData.target_audience.filter(t => t.trim()),
          industry_tags: formData.industry_tags.filter(t => t.trim())
        })
      });

      if (response.ok) {
        toast.success("Marketing angle created successfully!");
        setCreateMode(false);
        resetForm();
        fetchAngles();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create marketing angle");
      }
    } catch (error) {
      console.error('Error creating marketing angle:', error);
      toast.error("Failed to create marketing angle");
    }
  };

  const handleUpdateAngle = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/marketing/angles/${selectedAngle.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          target_audience: formData.target_audience.filter(t => t.trim()),
          industry_tags: formData.industry_tags.filter(t => t.trim())
        })
      });

      if (response.ok) {
        toast.success("Marketing angle updated successfully!");
        setEditMode(false);
        setSelectedAngle(null);
        resetForm();
        fetchAngles();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update marketing angle");
      }
    } catch (error) {
      console.error('Error updating marketing angle:', error);
      toast.error("Failed to update marketing angle");
    }
  };

  const handleDeleteAngle = async (angleId) => {
    if (!window.confirm('Are you sure you want to delete this marketing angle? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/marketing/angles/${angleId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success("Marketing angle deleted successfully!");
        fetchAngles();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to delete marketing angle");
      }
    } catch (error) {
      console.error('Error deleting marketing angle:', error);
      toast.error("Failed to delete marketing angle");
    }
  };

  const handleEditAngle = (angle) => {
    setSelectedAngle(angle);
    setFormData({
      title: angle.title,
      category: angle.category,
      source: angle.source,
      strategy_type: angle.strategy_type,
      dm_script: angle.dm_script || '',
      post_script: angle.post_script || '',
      video_script: angle.video_script || '',
      email_script: angle.email_script || '',
      target_audience: angle.target_audience || [],
      industry_tags: angle.industry_tags || [],
      effectiveness_score: angle.effectiveness_score,
      tone: angle.tone,
      access_level: angle.access_level,
      featured: angle.featured,
      active: angle.active
    });
    setEditMode(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      category: 'testimonial',
      source: 'custom',
      strategy_type: 'social_proof',
      dm_script: '',
      post_script: '',
      video_script: '',
      email_script: '',
      target_audience: [],
      industry_tags: [],
      effectiveness_score: 5.0,
      tone: 'professional',
      access_level: 'rookie',
      featured: false,
      active: true
    });
  };

  const handleArrayInput = (field, value) => {
    const array = value.split(',').map(item => item.trim()).filter(item => item);
    setFormData(prev => ({ ...prev, [field]: array }));
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'testimonial': Award,
      'problem_solution': Target,
      'manifestation': Lightbulb,
      'storytelling': BookOpen,
      'contrast': Users,
      'what_if': MessageSquare,
      'training': Video
    };
    return icons[category] || MessageSquare;
  };

  if (editMode || createMode) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">
              {createMode ? 'Create Marketing Angle' : 'Edit Marketing Angle'}
            </h1>
            <p className="text-gray-600">
              {createMode ? 'Add a new marketing angle to the library' : `Editing: ${selectedAngle?.title}`}
            </p>
          </div>
          <div className="flex space-x-2">
            <Button variant="outline" onClick={() => {
              setEditMode(false);
              setCreateMode(false);
              setSelectedAngle(null);
              resetForm();
            }}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button onClick={createMode ? handleCreateAngle : handleUpdateAngle}>
              <Save className="h-4 w-4 mr-2" />
              {createMode ? 'Create' : 'Update'} Angle
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Marketing angle title"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="category">Category *</Label>
                  <Select value={formData.category} onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="testimonial">Testimonial</SelectItem>
                      <SelectItem value="problem_solution">Problem-Solution</SelectItem>
                      <SelectItem value="manifestation">Manifestation</SelectItem>
                      <SelectItem value="storytelling">Storytelling</SelectItem>
                      <SelectItem value="contrast">Contrast</SelectItem>
                      <SelectItem value="what_if">What If</SelectItem>
                      <SelectItem value="training">Training</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="source">Source *</Label>
                  <Select value={formData.source} onValueChange={(value) => setFormData(prev => ({ ...prev, source: value }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="conversion_pack">Conversion Pack</SelectItem>
                      <SelectItem value="ad_vault">AdVault</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="tone">Tone</Label>
                  <Select value={formData.tone} onValueChange={(value) => setFormData(prev => ({ ...prev, tone: value }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="professional">Professional</SelectItem>
                      <SelectItem value="casual">Casual</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                      <SelectItem value="empathetic">Empathetic</SelectItem>
                      <SelectItem value="confident">Confident</SelectItem>
                      <SelectItem value="mystical">Mystical</SelectItem>
                      <SelectItem value="vulnerable">Vulnerable</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="access_level">Access Level *</Label>
                  <Select value={formData.access_level} onValueChange={(value) => setFormData(prev => ({ ...prev, access_level: value }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="boot_camp">Boot Camp</SelectItem>
                      <SelectItem value="rookie">Rookie</SelectItem>
                      <SelectItem value="pro">Pro</SelectItem>
                      <SelectItem value="enterprise">Enterprise</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="effectiveness_score">Effectiveness Score (0-10)</Label>
                <Input
                  id="effectiveness_score"
                  type="number"
                  min="0"
                  max="10"
                  step="0.1"
                  value={formData.effectiveness_score}
                  onChange={(e) => setFormData(prev => ({ ...prev, effectiveness_score: parseFloat(e.target.value) }))}
                />
              </div>

              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="featured"
                    checked={formData.featured}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, featured: checked }))}
                  />
                  <Label htmlFor="featured">Featured</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="active"
                    checked={formData.active}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, active: checked }))}
                  />
                  <Label htmlFor="active">Active</Label>
                </div>
              </div>

              <div>
                <Label htmlFor="target_audience">Target Audience (comma separated)</Label>
                <Input
                  id="target_audience"
                  value={formData.target_audience.join(', ')}
                  onChange={(e) => handleArrayInput('target_audience', e.target.value)}
                  placeholder="entrepreneurs, marketers, coaches"
                />
              </div>

              <div>
                <Label htmlFor="industry_tags">Industry Tags (comma separated)</Label>
                <Input
                  id="industry_tags"
                  value={formData.industry_tags.join(', ')}
                  onChange={(e) => handleArrayInput('industry_tags', e.target.value)}
                  placeholder="coaching, ecommerce, real_estate"
                />
              </div>
            </CardContent>
          </Card>

          {/* Scripts */}
          <Card>
            <CardHeader>
              <CardTitle>Marketing Scripts</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="dm">
                <TabsList className="grid grid-cols-4 w-full">
                  <TabsTrigger value="dm">DM Script</TabsTrigger>
                  <TabsTrigger value="post">Post Script</TabsTrigger>
                  <TabsTrigger value="video">Video Script</TabsTrigger>
                  <TabsTrigger value="email">Email Script</TabsTrigger>
                </TabsList>

                <TabsContent value="dm" className="space-y-2">
                  <Label>Direct Message Script</Label>
                  <Textarea
                    value={formData.dm_script}
                    onChange={(e) => setFormData(prev => ({ ...prev, dm_script: e.target.value }))}
                    rows={6}
                    placeholder="Enter DM script..."
                  />
                </TabsContent>

                <TabsContent value="post" className="space-y-2">
                  <Label>Social Media Post Script</Label>
                  <Textarea
                    value={formData.post_script}
                    onChange={(e) => setFormData(prev => ({ ...prev, post_script: e.target.value }))}
                    rows={6}
                    placeholder="Enter post script..."
                  />
                </TabsContent>

                <TabsContent value="video" className="space-y-2">
                  <Label>Video Script</Label>
                  <Textarea
                    value={formData.video_script}
                    onChange={(e) => setFormData(prev => ({ ...prev, video_script: e.target.value }))}
                    rows={6}
                    placeholder="Enter video script..."
                  />
                </TabsContent>

                <TabsContent value="email" className="space-y-2">
                  <Label>Email Script</Label>
                  <Textarea
                    value={formData.email_script}
                    onChange={(e) => setFormData(prev => ({ ...prev, email_script: e.target.value }))}
                    rows={6}
                    placeholder="Enter email script..."
                  />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Marketing Content Manager</h1>
          <p className="text-gray-600">Manage marketing angles, templates, and access permissions</p>
        </div>
        <Button onClick={() => setCreateMode(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Marketing Angle
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{angles.length}</div>
            <div className="text-sm text-gray-600">Total Angles</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{angles.filter(a => a.active).length}</div>
            <div className="text-sm text-gray-600">Active Angles</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-yellow-600">{angles.filter(a => a.featured).length}</div>
            <div className="text-sm text-gray-600">Featured Angles</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-purple-600">{templates.length}</div>
            <div className="text-sm text-gray-600">Templates</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search angles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger>
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="testimonial">Testimonial</SelectItem>
                <SelectItem value="problem_solution">Problem-Solution</SelectItem>
                <SelectItem value="manifestation">Manifestation</SelectItem>
                <SelectItem value="storytelling">Storytelling</SelectItem>
                <SelectItem value="contrast">Contrast</SelectItem>
                <SelectItem value="what_if">What If</SelectItem>
                <SelectItem value="training">Training</SelectItem>
              </SelectContent>
            </Select>

            <Select value={selectedSource} onValueChange={setSelectedSource}>
              <SelectTrigger>
                <SelectValue placeholder="All Sources" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="conversion_pack">Conversion Pack</SelectItem>
                <SelectItem value="ad_vault">AdVault</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>

            <Select value={selectedAccessLevel} onValueChange={setSelectedAccessLevel}>
              <SelectTrigger>
                <SelectValue placeholder="All Access Levels" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Levels</SelectItem>
                <SelectItem value="boot_camp">Boot Camp</SelectItem>
                <SelectItem value="rookie">Rookie</SelectItem>
                <SelectItem value="pro">Pro</SelectItem>
                <SelectItem value="enterprise">Enterprise</SelectItem>
              </SelectContent>
            </Select>

            <Button onClick={fetchAngles}>
              <Filter className="h-4 w-4 mr-2" />
              Apply Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Angles Table */}
      <Card>
        <CardHeader>
          <CardTitle>Marketing Angles</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading marketing angles...</div>
          ) : angles.length === 0 ? (
            <div className="text-center py-8">
              <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Marketing Angles Found</h3>
              <p className="text-gray-600 mb-4">Create your first marketing angle to get started</p>
              <Button onClick={() => setCreateMode(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Marketing Angle
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3">Title</th>
                    <th className="text-left p-3">Category</th>
                    <th className="text-left p-3">Source</th>
                    <th className="text-left p-3">Access Level</th>
                    <th className="text-left p-3">Score</th>
                    <th className="text-left p-3">Usage</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-right p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {angles.map((angle) => {
                    const CategoryIcon = getCategoryIcon(angle.category);
                    return (
                      <tr key={angle.id} className="border-b hover:bg-gray-50">
                        <td className="p-3">
                          <div className="flex items-center space-x-2">
                            <CategoryIcon className="h-4 w-4 text-gray-500" />
                            <div>
                              <div className="font-medium">{angle.title}</div>
                              <div className="text-sm text-gray-500">{angle.tone}</div>
                            </div>
                          </div>
                        </td>
                        <td className="p-3">
                          <Badge variant="outline">{angle.category}</Badge>
                        </td>
                        <td className="p-3">
                          <Badge className={angle.source === 'conversion_pack' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}>
                            {angle.source === 'conversion_pack' ? 'Conversion Pack' : angle.source === 'ad_vault' ? 'AdVault' : 'Custom'}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <Badge className={
                            angle.access_level === 'boot_camp' ? 'bg-yellow-100 text-yellow-800' :
                            angle.access_level === 'rookie' ? 'bg-green-100 text-green-800' :
                            angle.access_level === 'pro' ? 'bg-purple-100 text-purple-800' :
                            'bg-red-100 text-red-800'
                          }>
                            {angle.access_level}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center space-x-1">
                            <Star className="h-3 w-3 text-yellow-500" />
                            <span>{angle.effectiveness_score}/10</span>
                          </div>
                        </td>
                        <td className="p-3">{angle.usage_count}</td>
                        <td className="p-3">
                          <div className="flex items-center space-x-2">
                            {angle.active ? (
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            ) : (
                              <AlertCircle className="h-4 w-4 text-red-500" />
                            )}
                            <span className={angle.active ? 'text-green-600' : 'text-red-600'}>
                              {angle.active ? 'Active' : 'Inactive'}
                            </span>
                            {angle.featured && <Badge className="bg-yellow-100 text-yellow-800">Featured</Badge>}
                          </div>
                        </td>
                        <td className="p-3 text-right">
                          <div className="flex items-center justify-end space-x-2">
                            <Button size="sm" variant="outline" onClick={() => handleEditAngle(angle)}>
                              <Edit3 className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleDeleteAngle(angle.id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminMarketingManager;